import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Email, ArrowBack, CheckCircle } from '@mui/icons-material';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { THEME_COLORS, GRADIENTS, SHADOWS, BORDER_RADIUS } from '../../theme/themeConfig';
import ToastNotification from '../pages/common/Toastnotification';
import loginImage from '../../rawAssets/forgetPassword.png';

// Toast notification state interface
interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      showToast('Please enter your email address', 'error');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      showToast('Please enter a valid email address', 'error');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin + '/admin/login',
        handleCodeInApp: false,
      });
      setEmailSent(true);
      showToast('Password reset email sent! Check your inbox.', 'success');
    } catch (error: any) {
      console.error('Error sending reset email:', error);
      let errorMessage = 'Failed to send password reset email. Please try again.';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/admin/login');
  };

  const linkColor = isDark
    ? theme.palette.primary.dark
    : theme.palette.primary.main;

  const linkStyle: React.CSSProperties = {
    color: linkColor,
    textDecoration: 'none',
    fontWeight: 700,
  };

  // ── Decorative SVG accent shapes (identical to Login / Register) ──────────
  const AccentShapes = () => (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {/* Top-left large ring */}
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          left: -80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          border: `2px solid ${
            isDark
              ? 'rgba(255,255,255,0.06)'
              : `${theme.palette.primary.main}18`
          }`,
        }}
      />
      {/* Top-left small ring */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          left: -20,
          width: 140,
          height: 140,
          borderRadius: '50%',
          border: `1.5px solid ${
            isDark
              ? 'rgba(255,255,255,0.04)'
              : `${theme.palette.primary.main}22`
          }`,
        }}
      />
      {/* Bottom-right blob */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -60,
          right: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)'
            : `radial-gradient(circle, ${theme.palette.primary.main}0d 0%, transparent 70%)`,
        }}
      />
      {/* Tiny dot cluster top-right */}
      {[0, 1, 2].map((col) =>
        [0, 1, 2].map((row) => (
          <Box
            key={`dot-${col}-${row}`}
            sx={{
              position: 'absolute',
              top: 32 + row * 14,
              right: 40 + col * 14,
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.08)'
                : `${theme.palette.primary.main}33`,
            }}
          />
        ))
      )}
      {/* Bottom-left horizontal line accent */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 52,
          left: 32,
          width: 56,
          height: 2,
          borderRadius: 1,
          background: isDark
            ? 'rgba(255,255,255,0.07)'
            : `${theme.palette.primary.main}30`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          left: 32,
          width: 32,
          height: 2,
          borderRadius: 1,
          background: isDark
            ? 'rgba(255,255,255,0.04)'
            : `${theme.palette.primary.main}1a`,
        }}
      />
    </Box>
  );

  // ── Mobile layout ─────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
            backgroundColor: 'background.default',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: BORDER_RADIUS.lg,
              backgroundColor: 'background.paper',
              boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : SHADOWS.lg,
              border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            {!emailSent ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                {/* Back to Login Button */}
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Button
                    component={Link}
                    to="/admin/login"
                    startIcon={<ArrowBack />}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Back to Login
                  </Button>
                </Box>

                <Typography
                  component="h1"
                  variant="h4"
                  sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center', color: 'text.primary' }}
                >
                  Forgot Password?
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                  Enter your email address and we'll send you a link to reset your password.
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ mt: 2, mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>

                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Remember your password?{' '}
                      <Link to="/admin/login" style={linkStyle}>
                        Log In
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              // ── Success state ──────────────────────────────────────────────
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />

                <Typography component="h1" variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
                  Check Your Email
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  We've sent a password reset link to:
                </Typography>

                <Typography variant="body1" fontWeight="bold" sx={{ mb: 3, color: 'text.primary' }}>
                  {email}
                </Typography>

                <Alert severity="info" sx={{ width: '100%', mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2">
                    Click the link in the email to reset your password. The link will expire in 1 hour.
                  </Typography>
                </Alert>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Didn't receive the email? Check your spam folder or try again with a different email address.
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleBackToLogin}
                  sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                >
                  Back to Login
                </Button>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    onClick={() => { setEmailSent(false); setEmail(''); }}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Send to a different email
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Container>

        <ToastNotification
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={handleCloseToast}
        />
      </>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: 'background.default' }}>
        <Grid container sx={{ minHeight: '100vh' }}>

          {/* ── Left side — Form panel ─────────────────────────────────── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.default',
              padding: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Decorative background shapes */}
            <AccentShapes />

            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {/* ── Logo mark (identical to Login / Register) ──────────── */}
              <Box
                sx={{
                  mb: 5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 14px ${theme.palette.primary.main}55`,
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 2L17.32 6.5V15.5L10 20L2.68 15.5V6.5L10 2Z"
                      fill="white"
                      fillOpacity="0.9"
                    />
                    <path
                      d="M10 5.5L14.33 8V13L10 15.5L5.67 13V8L10 5.5Z"
                      fill="white"
                      fillOpacity="0.3"
                    />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    letterSpacing: '-0.02em',
                    color: 'text.primary',
                    lineHeight: 1,
                  }}
                >
                  Admin Portal
                </Typography>
              </Box>

              {!emailSent ? (
                <>
                  {/* ── Heading block ──────────────────────────────────── */}
                  <Box sx={{ mb: 4, width: '100%' }}>
                    {/* Back to Login link */}
                    <Button
                      component={Link}
                      to="/admin/login"
                      startIcon={<ArrowBack sx={{ fontSize: '0.9rem' }} />}
                      sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        mb: 2,
                        p: 0,
                        minWidth: 0,
                        fontSize: '0.85rem',
                        '&:hover': { background: 'none', textDecoration: 'underline' },
                      }}
                    >
                      Back to Login
                    </Button>

                    <Typography
                      component="h1"
                      sx={{
                        fontWeight: 800,
                        fontSize: '2rem',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.15,
                        color: 'text.primary',
                        mb: 0.75,
                      }}
                    >
                      Forgot password?
                    </Typography>
                    {/* Gradient underline accent */}
                    <Box
                      sx={{
                        height: 3,
                        width: 48,
                        borderRadius: 2,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.6 }}
                    >
                      Enter your email and we'll send you a reset link
                    </Typography>
                  </Box>

                  {/* ── Form ─────────────────────────────────────────────── */}
                  <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      placeholder="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2, '& .MuiInputLabel-root': { display: 'none' } }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{ mt: 2, mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Remember your password?{' '}
                        <Link to="/admin/login" style={linkStyle}>
                          Log In
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                // ── Success state ───────────────────────────────────────────
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                  }}
                >
                  {/* Success icon with primary-tinted background ring */}
                  <Box
                    sx={{
                      mb: 4,
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: isDark
                        ? 'rgba(255,255,255,0.05)'
                        : `${theme.palette.success.main}18`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 44, color: 'success.main' }} />
                  </Box>

                  <Typography
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: '2rem',
                      letterSpacing: '-0.03em',
                      lineHeight: 1.15,
                      color: 'text.primary',
                      mb: 0.75,
                    }}
                  >
                    Check your email
                  </Typography>
                  {/* Gradient underline accent */}
                  <Box
                    sx={{
                      height: 3,
                      width: 48,
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                      mb: 2,
                    }}
                  />

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.9rem' }}>
                    We've sent a password reset link to:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ mb: 3, color: 'text.primary' }}
                  >
                    {email}
                  </Typography>

                  <Alert severity="info" sx={{ width: '100%', mb: 3 }}>
                    <Typography variant="body2">
                      Click the link in the email to reset your password. The link will expire in 1 hour.
                    </Typography>
                  </Alert>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '0.85rem', lineHeight: 1.6 }}>
                    Didn't receive the email? Check your spam folder or try again with a different address.
                  </Typography>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleBackToLogin}
                    sx={{ mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                  >
                    Back to Login
                  </Button>

                  <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <Button
                      onClick={() => { setEmailSent(false); setEmail(''); }}
                      sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.875rem' }}
                    >
                      Send to a different email
                    </Button>
                  </Box>
                </Box>
              )}

              {/* ── Footer note (identical to Login / Register) ────────── */}
              <Box
                sx={{
                  mt: 6,
                  pt: 3,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.success.main,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                  Secured with end-to-end encryption
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* ── Right side — Image panel (identical to Login) ─────────────── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundImage: `url(${loginImage})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Multi-layer overlay for depth */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: isDark
                  ? 'linear-gradient(160deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%)'
                  : 'linear-gradient(160deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.52) 100%)',
              }}
            />

            {/* Primary colour tint strip — left edge */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 5,
                height: '100%',
                background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                opacity: 0.85,
              }}
            />

            {/* Top-right decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                border: '1.5px solid rgba(255,255,255,0.12)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                width: 110,
                height: 110,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.08)',
                pointerEvents: 'none',
              }}
            />
          </Grid>

        </Grid>
      </Box>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default ForgotPassword;